import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getUrlParams } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import GOQiiLoader from "../Components/GOQiiLoader";
import "./Theme.css";
import "./FoodLogInsight.css";

const chartTextStyle = { fontFamily: "'Open Sans', sans-serif" };
// The live page's analytics events use "Macro" (singular) — kept distinct
// from the tab's own display label ("Macros").
const TAB_EVENT_ACTIONS = ["Nutrients", "Macro", "Calories"];

const NoData = ({ text }) => <div className="fli-no-data">{text}</div>;

// Shared "Last 7 Days X" bar chart with a dashed target line — used by both
// the Macros tab (protein/fats/carbs) and Calories tab (calorie intake).
const WeeklyTrendChart = ({ title, dates, values, target, targetLabel, color, unit }) => {
  if (!dates || !dates.length) return null;

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 180,
      style: chartTextStyle,
    },
    title: { text: null },
    xAxis: {
      categories: dates,
      labels: { style: { color: "var(--text-muted)", fontSize: "11px" } },
      lineColor: "var(--border-subtle)",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: null },
      gridLineColor: "var(--border-subtle)",
      labels: { style: { color: "var(--text-muted)", fontSize: "10px" } },
      plotLines: target
        ? [
            {
              value: target,
              color: "#4A90D9",
              dashStyle: "Dash",
              width: 1,
              zIndex: 4,
              label: {
                text: targetLabel,
                align: "right",
                style: { color: "var(--text-muted)", fontSize: "10px" },
              },
            },
          ]
        : [],
    },
    series: [{ name: title, data: values, color }],
    legend: { enabled: false },
    tooltip: { enabled: false, pointFormat: `<b>{point.y} ${unit}</b>` },
    credits: { enabled: false },
    plotOptions: {
      series: { animation: false },
      column: { borderRadius: 4, borderWidth: 0, pointWidth: 14 },
    },
  };

  return (
    <section className="fli-tab-section">
      <h3 className="fli-section-title">{title}</h3>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </section>
  );
};

const CaloriesTab = ({ data, t }) => {
  if (!data || data.code !== 200) {
    return <NoData text={t("foodLogInsight.noData")} />;
  }

  const { caloriespercentage, calories, caloriesDates, caloriesTotal, caloriesTarget } = data.data;
  const totalCalories = calories.reduce((sum, v) => sum + (v || 0), 0);

  const options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 220,
      style: chartTextStyle,
    },
    title: { text: null },
    plotOptions: {
      pie: {
        colors: ["#FFAC6F", "#F56666", "#FFE783", "#88C8F7"],
        borderWidth: 0,
        innerSize: "0%",
        dataLabels: {
          enabled: true,
          format: "{point.y}%",
          distance: -25,
          style: { fontSize: "10px", textOutline: "none" },
        },
      },
    },
    tooltip: {
      enabled: false,
      formatter() {
        return `<b>${this.point.name}: ${this.point.y}%</b>`;
      },
    },
    series: [
      {
        name: t("foodLogInsight.tabs.calories"),
        data: [
          { name: t("foodLogInsight.calories.breakfast"), y: caloriespercentage[0] },
          { name: t("foodLogInsight.calories.lunch"), y: caloriespercentage[1] },
          { name: t("foodLogInsight.calories.snack"), y: caloriespercentage[2] },
          { name: t("foodLogInsight.calories.dinner"), y: caloriespercentage[3] },
        ],
      },
    ],
    credits: { enabled: false },
  };

  return (
    <>
      <section className="fli-tab-section">
        <h3 className="fli-section-title fli-text-center">{t("foodLogInsight.calories.overallTitle")}</h3>
        <div className="fli-calories-row">
          <ul className="fli-legend-list">
            <li>
              <span className="fli-dot fli-dot-orange" />
              {t("foodLogInsight.calories.breakfast")}
              <span className="fli-legend-value">{calories[0]} {t("foodLogInsight.calories.cal")}</span>
            </li>
            <li>
              <span className="fli-dot fli-dot-blue" />
              {t("foodLogInsight.calories.dinner")}
              <span className="fli-legend-value">{calories[3]} {t("foodLogInsight.calories.cal")}</span>
            </li>
          </ul>

          <div className="fli-chart-wrap fli-pie-wrap">
            <HighchartsReact highcharts={Highcharts} options={options} />
            <div className="fli-pie-center">
              <div className="fli-pie-center-value">{totalCalories}</div>
              <div className="fli-pie-center-label">{t("foodLogInsight.calories.totalCalories")}</div>
            </div>
          </div>

          <ul className="fli-legend-list">
            <li>
              <span className="fli-dot fli-dot-red" />
              {t("foodLogInsight.calories.lunch")}
              <span className="fli-legend-value">{calories[1]} {t("foodLogInsight.calories.cal")}</span>
            </li>
            <li>
              <span className="fli-dot fli-dot-yellow" />
              {t("foodLogInsight.calories.snack")}
              <span className="fli-legend-value">{calories[2]} {t("foodLogInsight.calories.cal")}</span>
            </li>
          </ul>
        </div>
      </section>

      <WeeklyTrendChart
        title={t("foodLogInsight.calories.weeklyTitle")}
        dates={caloriesDates}
        values={caloriesTotal}
        target={caloriesTarget}
        targetLabel={`${t("foodLogInsight.calories.targetLabel")}: ${caloriesTarget} ${t("foodLogInsight.calories.cal")}`}
        color="#4FC3C7"
        unit={t("foodLogInsight.calories.cal")}
      />
    </>
  );
};

// "Last 7 days Macronutrients Ratio %" — the one section fed by the second
// endpoint (nutrients_macros), lazily fetched only while the Macros tab is
// mounted.
const RatioChart = ({ dates, nutrientTotals, t }) => {
  if (!dates || !nutrientTotals) return null;

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 180,
      style: chartTextStyle,
    },
    title: { text: null },
    xAxis: {
      categories: dates,
      labels: { style: { color: "var(--text-muted)", fontSize: "11px" } },
      lineColor: "var(--border-subtle)",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: null },
      max: 100,
      gridLineColor: "var(--border-subtle)",
      labels: { style: { color: "var(--text-muted)", fontSize: "10px" } },
    },
    plotOptions: {
      column: { stacking: "normal", borderWidth: 0 },
      series: { animation: false },
    },
    colors: ["#7EE5B5", "#FFE783", "#F86B6B"],
    series: [
      { name: t("foodLogInsight.macros.protein"), data: nutrientTotals.map((d) => d[0]) },
      { name: t("foodLogInsight.macros.fats"), data: nutrientTotals.map((d) => d[1]) },
      { name: t("foodLogInsight.macros.carbs"), data: nutrientTotals.map((d) => d[2]) },
    ],
    legend: { enabled: false },
    tooltip: {
      enabled: false,
      shared: true,
      pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}%</b><br/>',
    },
    credits: { enabled: false },
  };

  return (
    <section className="fli-tab-section">
      <h3 className="fli-section-title">{t("foodLogInsight.macros.ratioTitle")}</h3>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </section>
  );
};

const MacrosTab = ({ data, date, t }) => {
  const [unit, setUnit] = useState("percent");
  const [ratioData, setRatioData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    lifestyleApi
      .foodLogNutrientsMacros(date)
      .then((res) => {
        if (!cancelled && res.data?.code === 200) setRatioData(res.data.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [date]);

  if (!data || data.code !== 200) {
    return <NoData text={t("foodLogInsight.noData")} />;
  }

  const {
    macros,
    proteinDates, proteinTotal, proteingm,
    fatDates, fatTotal, fatgm,
    carbsDates, carbsTotal, carbsgm,
  } = data.data;
  const isPercent = unit === "percent";

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 260,
      style: chartTextStyle,
    },
    title: { text: null },
    xAxis: {
      categories: [
        t("foodLogInsight.macros.breakfast"),
        t("foodLogInsight.macros.lunch"),
        t("foodLogInsight.macros.snacks"),
        t("foodLogInsight.macros.dinner"),
      ],
      labels: { style: { color: "var(--text-muted)" } },
      lineColor: "var(--border-subtle)",
    },
    yAxis: {
      title: { text: null },
      max: isPercent ? 100 : null,
      labels: { style: { color: "var(--text-muted)" } },
      gridLineColor: "var(--border-subtle)",
    },
    tooltip: {
      enabled: false,
      pointFormat: `<span style="color:{series.color}">{series.name}</span>: <b>{point.y}${isPercent ? "%" : t("foodLogInsight.macros.calUnit")}</b><br/>`,
      shared: true,
    },
    plotOptions: {
      column: { stacking: "total", borderRadius: 10, borderWidth: 0 },
      series: { pointWidth: 14 },
    },
    colors: ["#7EE5B5", "#FFE783", "#F86B6B"],
    series: [
      { name: t("foodLogInsight.macros.protein"), data: isPercent ? macros.proteinpercent : macros.protein },
      { name: t("foodLogInsight.macros.fats"), data: isPercent ? macros.fatspercent : macros.fats },
      { name: t("foodLogInsight.macros.carbs"), data: isPercent ? macros.carbspercent : macros.carbs },
    ],
    legend: { enabled: false },
    credits: { enabled: false },
  };

  const rows = [
    { label: t("foodLogInsight.macros.breakfast"), idx: 0 },
    { label: t("foodLogInsight.macros.lunch"), idx: 1 },
    { label: t("foodLogInsight.macros.snacks"), idx: 2 },
    { label: t("foodLogInsight.macros.dinner"), idx: 3 },
  ];

  return (
    <>
      <section className="fli-tab-section">
        <div className="fli-macros-header">
          <h3 className="fli-section-title">{t("foodLogInsight.macros.overviewTitle")}</h3>
          <div className="fli-switch-field">
            <button
              type="button"
              className={isPercent ? "fli-switch-btn fli-switch-btn--active" : "fli-switch-btn"}
              onClick={() => setUnit("percent")}
            >
              %
            </button>
            <button
              type="button"
              className={!isPercent ? "fli-switch-btn fli-switch-btn--active" : "fli-switch-btn"}
              onClick={() => setUnit("cal")}
            >
              {t("foodLogInsight.macros.calUnit")}
            </button>
          </div>
        </div>

        <ul className="fli-legend-list fli-legend-list--inline">
          <li><span className="fli-dot fli-dot-green" /> {t("foodLogInsight.macros.protein")}</li>
          <li><span className="fli-dot fli-dot-yellow" /> {t("foodLogInsight.macros.carbs")}</li>
          <li><span className="fli-dot fli-dot-red" /> {t("foodLogInsight.macros.fats")}</li>
        </ul>

        <HighchartsReact highcharts={Highcharts} options={options} />
      </section>

      <RatioChart
        dates={ratioData?.caloriesDates}
        nutrientTotals={ratioData?.nutrientTotals}
        t={t}
      />

      <WeeklyTrendChart
        title={t("foodLogInsight.macros.weeklyProteinTitle")}
        dates={proteinDates}
        values={proteinTotal}
        target={proteingm}
        targetLabel={`${t("foodLogInsight.macros.targetProtein")}: ${proteingm} ${t("foodLogInsight.macros.gramUnit")}`}
        color="#8C7AE6"
        unit={t("foodLogInsight.macros.gramUnit")}
      />
      <WeeklyTrendChart
        title={t("foodLogInsight.macros.weeklyFatsTitle")}
        dates={fatDates}
        values={fatTotal}
        target={fatgm}
        targetLabel={`${t("foodLogInsight.macros.targetFats")}: ${fatgm} ${t("foodLogInsight.macros.gramUnit")}`}
        color="#F5A623"
        unit={t("foodLogInsight.macros.gramUnit")}
      />
      <WeeklyTrendChart
        title={t("foodLogInsight.macros.weeklyCarbsTitle")}
        dates={carbsDates}
        values={carbsTotal}
        target={carbsgm}
        targetLabel={`${t("foodLogInsight.macros.targetCarbs")}: ${carbsgm} ${t("foodLogInsight.macros.gramUnit")}`}
        color="#26C6C0"
        unit={t("foodLogInsight.macros.gramUnit")}
      />

      <section className="fli-tab-section">
        <h3 className="fli-section-title">{t("foodLogInsight.macros.breakdownTitle")}</h3>
        <table className="fli-table">
          <tbody>
            {rows.map((row) => (
              <tr key={row.idx}>
                <td>
                  <strong>{row.label}</strong>
                  <div className="fli-table-sub">
                    {t("foodLogInsight.macros.carbs")} {macros.carbs[row.idx]} {t("foodLogInsight.macros.calUnit")}
                  </div>
                </td>
                <td>{t("foodLogInsight.macros.protein")} {macros.protein[row.idx]} {t("foodLogInsight.macros.calUnit")}</td>
                <td>{t("foodLogInsight.macros.fats")} {macros.fats[row.idx]} {t("foodLogInsight.macros.calUnit")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
};

const NUTRIENT_ORDER = [
  "protein", "carbohydrate", "fiber", "sugar", "fat", "saturatedFat",
  "polyunsaturatedFat", "monounsaturatedFat", "transFat", "cholesterol",
  "sodium", "potassium", "iron", "calcium", "vitaminA", "vitaminC",
];

const NutrientsTab = ({ data, t }) => {
  if (!data || data.code !== 200) {
    return <NoData text={t("foodLogInsight.noData")} />;
  }

  const { nutrients } = data.data;

  return (
    <section className="fli-tab-section">
      <h3 className="fli-section-title">{t("foodLogInsight.nutrients.title")}</h3>
      <table className="fli-table fli-table--striped">
        <tbody>
          {NUTRIENT_ORDER.map((key) => (
            <tr key={key}>
              <td>{t(`foodLogInsight.nutrients.${key}`)}</td>
              <td className="fli-text-end">{nutrients[key]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const FoodLogInsight = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const [today] = useState(() => new Date().toISOString().split("T")[0]);

  const [tabIndex, setTabIndex] = useState(0);
  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    let cancelled = false;
    lifestyleApi
      .foodLogNutrients(today)
      .then((res) => {
        if (!cancelled) setLogData(res.data);
      })
      .catch(() => {
        if (!cancelled) setLogData({ code: 404 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [today]);

  // Matches the live page firing one analytics event per tab view (including
  // the default tab on first load), with Lifestyle reflecting whatever the
  // food-log fetch has resolved to by the time the tab is viewed.
  useEffect(() => {
    lifestyleApi
      .logFoodInsightEvent(TAB_EVENT_ACTIONS[tabIndex], logData?.data?.lifestyle || " ")
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  return (
    <section className="fli-page">
      <GOQiiLoader visible={loading} size={70} />

      <div className="fli-card">
        <Tabs selectedIndex={tabIndex} onSelect={setTabIndex}>
          <TabList className="fli-tab-list">
            <Tab className="fli-tab" selectedClassName="fli-tab--active">{t("foodLogInsight.tabs.nutrients")}</Tab>
            <Tab className="fli-tab" selectedClassName="fli-tab--active">{t("foodLogInsight.tabs.macros")}</Tab>
            <Tab className="fli-tab" selectedClassName="fli-tab--active">{t("foodLogInsight.tabs.calories")}</Tab>
          </TabList>

          <TabPanel>
            <NutrientsTab data={logData} t={t} />
          </TabPanel>
          <TabPanel>
            <MacrosTab data={logData} date={today} t={t} />
          </TabPanel>
          <TabPanel>
            <CaloriesTab data={logData} t={t} />
          </TabPanel>
        </Tabs>
      </div>
    </section>
  );
};

export default FoodLogInsight;
